import { Trans, Plural, useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { EditDoubleStaffing } from "./EditDoubleStaffing";
import { EditButton } from "./EditButton";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { useCurrentlyEditing } from "./useCurrentlyEditing";
import { EditTitle } from "./EditTitle";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { EditDescription } from "./EditDescription";
import { EditMeasurements } from "./EditMeasurements";
import MeasurementChip from "@/components/Chips/MeasurementChip";
import { EditTime } from "./EditTime";
import { EditHidden } from "./EditHidden";
import { EditRequiredCompetences } from "./EditRequiredCompetences";
import {
  type IActivity,
  useActivity,
  useActivityOccurrence,
} from "@/api/Activities";
import { ActivitySchedule } from "./ActivitySchedule";
import {
  getActivityCategoryConfig,
  isEmployeePerformed,
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { dateName } from "@/Utils/DateUtils";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import styles from "./ActivityOccurrenceInformation.module.scss";
import { EditContainer } from "./EditContainer";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import * as Sentry from "@sentry/react";
import { EditInvoicingCodes } from "@/pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/EditInvoicingCodes";
import { useActivityInvoicingCodes } from "@/api/Invoicing";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { ActivityEndDate } from "./ActivityEndDate";
import { EditDuration } from "./EditDuration";
import { formatDuration } from "@/forms/AddActivityForm/activityDurationUtils";
import { useLocation, useNavigate } from "react-router";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";

export const DurationText = ({ duration }: { duration: number }) => {
  const { value, timeUnit } = formatDuration(duration);
  if (timeUnit === "hour") {
    return <Plural value={value} one="# timme" other="# timmar" />;
  }
  return <Plural value={value} one="# minut" other="# minuter" />;
};

export const InvoicingCodes = ({ activity }: { activity: IActivity }) => {
  const { data: activityInvoicingCodes } = useActivityInvoicingCodes(
    activity.id,
  );
  const { currentlyEditing, setCurrentlyEditing } = useCurrentlyEditing();

  return (
    <section className={styles.informationSection}>
      <div className={styles.editableSection}>
        <Heading level="h3">
          <Trans>Faktureringskoder</Trans>
        </Heading>
        <EditButton
          attribute="edit-invoicingCodes"
          isEditing={currentlyEditing === "invoicingCodes"}
          toggleIsEditing={() =>
            setCurrentlyEditing(
              currentlyEditing === "invoicingCodes" ? null : "invoicingCodes",
            )
          }
        />
      </div>
      {currentlyEditing === "invoicingCodes" && activity.patientId ? (
        <EditContainer>
          <EditInvoicingCodes
            currentProcedureCodes={
              activityInvoicingCodes?.procedureCodes.map((code) => code.id) ??
              []
            }
            currentProductCodes={
              activityInvoicingCodes?.productCodes.map((code) => code.id) ?? []
            }
            onSuccess={() => setCurrentlyEditing(null)}
            activityId={activity.id}
            patientId={activity.patientId}
          />
        </EditContainer>
      ) : activityInvoicingCodes &&
        (activityInvoicingCodes.procedureCodes.length > 0 ||
          activityInvoicingCodes.productCodes.length > 0) ? (
        <>
          <Text element="span">
            {activityInvoicingCodes.procedureCodes.length > 0 && (
              <>
                <Trans>Åtgärd (KVÅ)</Trans>:
                {activityInvoicingCodes.procedureCodes
                  .map(
                    (procedureCode) =>
                      ` ${procedureCode.code} - ${procedureCode.name}`,
                  )
                  .join(", ")}
              </>
            )}
          </Text>
          <Text element="span">
            {activityInvoicingCodes.productCodes.length > 0 && (
              <>
                <Trans>Produkt:</Trans>
                {activityInvoicingCodes.productCodes
                  .map(
                    (productCode) =>
                      ` ${productCode.code} - ${productCode.name}`,
                  )
                  .join(", ")}
              </>
            )}
          </Text>
        </>
      ) : (
        <Trans>Ingen</Trans>
      )}
    </section>
  );
};

export const ActivityOccurrenceInformation = ({
  isRecurringTab,
  activityId,
  occurrenceId,
}: {
  isRecurringTab: boolean;
  activityId: string;
  occurrenceId: string;
}) => {
  const { t } = useLingui();
  const { currentlyEditing, setCurrentlyEditing } = useCurrentlyEditing();

  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const { data: activity, isPending, isError, error } = useActivity(activityId);

  const {
    data: occurrence,
    isPending: occurrenceIsPending,
    isError: occurrenceIsError,
    error: occurrenceError,
  } = useActivityOccurrence(activityId, occurrenceId);

  const { organization } = useUserProfile();

  const navigate = useNavigate();
  const location = useLocation();

  if (isPending || occurrenceIsPending) {
    return <Loading message={t`Hämtar aktivitet`} padding={24} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return <ErrorMessage message={deducedError(error)} padding={24} />;
  }

  if (occurrenceIsError) {
    Sentry.captureException(occurrenceError);
    return (
      <ErrorMessage message={deducedError(occurrenceError)} padding={24} />
    );
  }

  const isRecurringActivity = activity.recurring;
  const isSingleActivityNoRecurrency = !isRecurringActivity && !isRecurringTab;
  const isSingleActivityWithRecurrency = isRecurringActivity && !isRecurringTab;
  const isSingleOccurrenceOrRecurring =
    isRecurringTab || isSingleActivityNoRecurrency;
  const activityHasPatient = Boolean(activity.patientId);

  const {
    allowsHidden,
    allowsCompetence,
    requiresCompetence,
    allowsInvoicingCode: activityAllowsInvoicingCode,
    allowsDuration,
    allowsDoubleStaffing,
  } = getActivityCategoryConfig({
    category: activity.category,
    activityHasPatient,
  });

  const activityOccurrenceMayHaveInvoicingCodes =
    activityAllowsInvoicingCode && organizationUsesInvoicingCodes(organization);
  const showActivityDuration = allowsDuration && showDuration;

  return (
    <div className={styles.activityInfo}>
      {isSingleOccurrenceOrRecurring ? (
        <>
          <section className={styles.informationSection}>
            <div className={styles.editableSection}>
              <Heading level="h3">
                <Trans>Namn</Trans>
              </Heading>
              <EditButton
                attribute="edit-title"
                isEditing={currentlyEditing === "title"}
                toggleIsEditing={() =>
                  setCurrentlyEditing(
                    currentlyEditing === "title" ? null : "title",
                  )
                }
              />
            </div>
            {currentlyEditing === "title" ? (
              <EditContainer>
                <EditTitle
                  currentTitle={activity.title}
                  onSuccess={() => setCurrentlyEditing(null)}
                  activityId={activity.id}
                />
              </EditContainer>
            ) : (
              <Text element="p">{activity.title}</Text>
            )}
          </section>
          <section className={styles.informationSection}>
            <div className={styles.editableSection}>
              <Heading level="h3">
                <Trans>Beskrivning</Trans>
              </Heading>
              <EditButton
                attribute="edit-description"
                isEditing={currentlyEditing === "description"}
                toggleIsEditing={() =>
                  setCurrentlyEditing(
                    currentlyEditing === "description" ? null : "description",
                  )
                }
              />
            </div>
            {currentlyEditing === "description" ? (
              <EditContainer>
                <EditDescription
                  currentDescription={activity.description}
                  onSuccess={() => setCurrentlyEditing(null)}
                  activityId={activity.id}
                />
              </EditContainer>
            ) : (
              <TextWithLineBreaks>{activity.description}</TextWithLineBreaks>
            )}
          </section>
          {"measurements" in activity ? (
            <section className={styles.informationSection}>
              <div className={styles.editableSection}>
                <Heading level="h3">
                  <Trans>Mätvärden</Trans>
                </Heading>
                <EditButton
                  attribute="edit-measurements"
                  isEditing={currentlyEditing === "measurements"}
                  toggleIsEditing={() =>
                    setCurrentlyEditing(
                      currentlyEditing === "measurements"
                        ? null
                        : "measurements",
                    )
                  }
                />
              </div>
              {currentlyEditing === "measurements" ? (
                <EditContainer>
                  <EditMeasurements
                    currentMeasurements={activity.measurements}
                    onSuccess={() => setCurrentlyEditing(null)}
                    activityId={activity.id}
                  />
                </EditContainer>
              ) : (
                <ul className={styles.chips}>
                  {activity.measurements.map((measurement) => (
                    <li key={measurement}>
                      <MeasurementChip measurement={measurement} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </>
      ) : null}

      {isRecurringTab && "times" in activity.schedule ? (
        <>
          <ActivityEndDate
            activityId={activityId}
            status={activity.status}
            schedule={activity.schedule}
          />
          <ActivitySchedule
            activityId={activityId}
            onEditScheduleSuccess={() => {
              // Since the schedule determines the occurrenceId (which is part of the URL),
              // it is important to navigate away from the detail view when the schedule is changed.
              // This is only applicable when the occurrenceId is part of the current URL.
              if (
                occurrenceId &&
                location.pathname.endsWith(
                  `${activityId}/occurrences/${occurrenceId}`,
                )
              ) {
                // Navigate to the parent of the current route (see `index.tsx` for route structure)
                void navigate("..");
              }
            }}
            schedule={activity.schedule}
          />
        </>
      ) : (
        <section className={styles.informationSection}>
          <div className={styles.editableSection}>
            <Heading level="h3">
              <Trans>Starttid</Trans>
            </Heading>
            <EditButton
              attribute="edit-time"
              label={t`Ändra`}
              isEditing={currentlyEditing === "time"}
              toggleIsEditing={() =>
                setCurrentlyEditing(currentlyEditing === "time" ? null : "time")
              }
            />
          </div>
          {currentlyEditing === "time" ? (
            <EditContainer>
              <EditTime
                currentDateTime={occurrence.start}
                currentTimeOfDay={occurrence.timeOfDay}
                currentTimespan={occurrence.timespan}
                onSuccess={() => {
                  setCurrentlyEditing(null);
                }}
                activityId={activityId}
                occurrenceId={occurrenceId}
              />
            </EditContainer>
          ) : (
            <Text element="p">
              {dateName(occurrence.start)}
              <br />
              {occurrence.timeOfDay === timeOfDaySchema.enum.Any
                ? t(timeOfDayDictionary.Any.long)
                : formattedTimeSpan(occurrence.start, occurrence.end)}
            </Text>
          )}
        </section>
      )}

      {isSingleOccurrenceOrRecurring && showActivityDuration ? (
        <section className={styles.informationSection}>
          <div className={styles.editableSection}>
            <Heading level="h3">
              <Trans>Tidsåtgång</Trans>
            </Heading>
            <EditButton
              attribute="edit-duration"
              label={t`Ändra`}
              isEditing={currentlyEditing === "duration"}
              toggleIsEditing={() =>
                setCurrentlyEditing(
                  currentlyEditing === "duration" ? null : "duration",
                )
              }
            />
          </div>
          {currentlyEditing === "duration" ? (
            <EditContainer>
              <EditDuration
                currentDuration={activity.duration}
                onSuccess={() => {
                  setCurrentlyEditing(null);
                }}
                activityId={activityId}
              />
            </EditContainer>
          ) : (
            <Text element="p">
              <DurationText duration={activity.duration} />
            </Text>
          )}
        </section>
      ) : null}

      {!isSingleActivityWithRecurrency ? (
        <>
          {allowsHidden ? (
            <section className={styles.informationSection}>
              <div className={styles.editableSection}>
                <Heading level="h3">
                  <Trans>Dold för patienten</Trans>
                </Heading>
                <EditButton
                  attribute="edit-hidden"
                  isEditing={currentlyEditing === "hidden"}
                  toggleIsEditing={() =>
                    setCurrentlyEditing(
                      currentlyEditing === "hidden" ? null : "hidden",
                    )
                  }
                />
              </div>
              {currentlyEditing === "hidden" ? (
                <EditContainer>
                  <EditHidden
                    currentHidden={activity.hidden}
                    onSuccess={() => setCurrentlyEditing(null)}
                    activityId={activity.id}
                  />
                </EditContainer>
              ) : (
                <Text element="p">{activity.hidden ? t`Ja` : t`Nej`}</Text>
              )}
            </section>
          ) : null}
          {allowsCompetence && isEmployeePerformed(activity) ? (
            <section className={styles.informationSection}>
              <div className={styles.editableSection}>
                <Heading level="h3">
                  <Trans>Kompetensbehov</Trans>
                </Heading>
                <EditButton
                  attribute="edit-requiredCompetences"
                  isEditing={currentlyEditing === "requiredCompetences"}
                  toggleIsEditing={() =>
                    setCurrentlyEditing(
                      currentlyEditing === "requiredCompetences"
                        ? null
                        : "requiredCompetences",
                    )
                  }
                />
              </div>
              {currentlyEditing === "requiredCompetences" ? (
                <EditContainer>
                  <EditRequiredCompetences
                    currentRequiredCompetences={activity.requiredCompetences}
                    isRequired={requiresCompetence}
                    onSuccess={() => setCurrentlyEditing(null)}
                    activityId={activity.id}
                  />
                </EditContainer>
              ) : (
                <ul className={styles.chips}>
                  {activity.requiredCompetences.map((competence) => (
                    <li key={competence}>
                      <CompetenceChip competence={competence} state="neutral" />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
          {allowsDoubleStaffing && "doubleStaffing" in activity ? (
            <section className={styles.informationSection}>
              <div className={styles.editableSection}>
                <Heading level="h3">
                  <Trans>Dubbelbemanning</Trans>
                </Heading>
                <EditButton
                  attribute="edit-doubleStaffing"
                  isEditing={currentlyEditing === "doubleStaffing"}
                  toggleIsEditing={() =>
                    setCurrentlyEditing(
                      currentlyEditing === "doubleStaffing"
                        ? null
                        : "doubleStaffing",
                    )
                  }
                />
              </div>
              {currentlyEditing === "doubleStaffing" ? (
                <EditContainer>
                  <EditDoubleStaffing
                    currentDoubleStaffing={activity.doubleStaffing}
                    onSuccess={() => setCurrentlyEditing(null)}
                    activityId={activity.id}
                  />
                </EditContainer>
              ) : (
                <Text element="p">
                  {activity.doubleStaffing ? t`Ja` : t`Nej`}
                </Text>
              )}
            </section>
          ) : null}

          {activityOccurrenceMayHaveInvoicingCodes ? (
            <InvoicingCodes activity={activity} />
          ) : null}
        </>
      ) : null}
    </div>
  );
};
